import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import api from "../../utils/api";
import Logo from "../../assets/logo.jpg";

export default function PLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", role: "patient" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/auth/login", form);

      const user = response?.user ?? response?.data?.user;
      const token = response?.token ?? response?.data?.token;

      if (!user || !token) {
        throw new Error("Invalid response from server");
      }

      login(user, token);

      const redirectMap = {
        admin: "/admin",
        doctor: "/doctor",
        patient: "/patient",
        lab: "/lab",
      };

      toast.success(`Welcome back, ${user.name || "Patient"}!`);
      navigate(redirectMap[user.role] || "/patient", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Brand / Logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <Link to="/" className="mb-2">
            <img
              src={Logo}
              alt="Docjee"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <p className="text-slate-500 mt-1 text-sm">Patient Portal — Book Doctors, Test</p>
        </div>

        <div className="card bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
          <h2 className="font-display font-semibold text-xl text-slate-800 mb-6">
            Login
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                className="input-field w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-700 outline-none focus:border-emerald-600 focus:bg-white"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="input-field w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-700 outline-none focus:border-emerald-600 focus:bg-white"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-2 rounded-full bg-emerald-600 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-70"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 space-y-3 text-center text-sm text-slate-500">
            <p>
              Don't have an account?{" "}
              <Link
                to="/register/patient"
                className="text-emerald-600 hover:underline font-medium"
              >
                Register
              </Link>
            </p>
            <div className="flex items-center justify-center gap-4 text-xs font-medium text-slate-600 pt-2">
              <Link to="/" className="hover:text-emerald-600 transition-colors">
                Home
              </Link>
              <span>•</span>
              <Link to="/login/doctor-lab" className="hover:text-emerald-600 transition-colors">
                Doctor & Lab Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}